import { DocumentType } from '@bulthaup/api/db';
import { Button, Divider, Group, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconEye, IconTrash } from '@tabler/icons-react';
import { reverse } from 'lodash';

import { ServiceOrderForm } from '../../-components/service-order/-service-order-form';
import { useUser } from '../../../../../../hooks/use-user';
import { RouterOutput, trpc } from '../../../../../../services/trpc';
import { getUTDocumentUrl } from '../../../../../../services/upload';
import { formatDate } from '../../../../../../utils/format';

export function ServiceOrderRequestSection({
  project,
}: {
  project: NonNullable<RouterOutput['projects']['get']>;
}) {
  const { user } = useUser();
  const deleteServiceOrderRequestMutation =
    trpc.projects.deleteServiceOrderRequest.useMutation();

  const deleteServiceOrder = trpc.documents.deleteServiceOrder.useMutation({
    onSuccess: () => {
      notifications.show({
        title: "Fiche d'intervention supprimée",
        message: '',
        color: 'green',
      });
    },
  });

  const generateServiceOrder = trpc.documents.generateServiceOrder.useMutation({
    onSuccess: async (_, variables) => {
      // Delete the ServiceOrderRequest after successfully creating the ServiceOrder document
      await deleteServiceOrderRequestMutation.mutateAsync({
        id: variables.serviceOrderRequestId,
      });
      modals.closeAll();
      notifications.show({
        title: "Fiche d'intervention remplie avec succès",
        message: '',
        color: 'green',
      });
    },
  });

  const serviceOrderRequests = project.serviceOrderRequests || [];
  const serviceOrderDocuments = project.documents.filter(
    document => document.type === ('SERVICE_ORDER' as DocumentType),
  );

  const openFillOutModal = (
    serviceOrderRequest: NonNullable<
      RouterOutput['projects']['get']
    >['serviceOrderRequests'][0],
  ) => {
    modals.open({
      title: "Remplir la fiche d'intervention",
      size: 'xl',
      children: (
        <ServiceOrderForm
          project={project}
          serviceOrderRequest={serviceOrderRequest}
          onSubmit={async values => {
            await generateServiceOrder.mutateAsync({
              projectId: project.id,
              serviceOrderRequestId: serviceOrderRequest.id,
              date: values.date ? new Date(values.date) : undefined,
              time: values.time,
              attendees: values.attendees,
              extraAttendee: values.extraAttendee,
              answer1: values.answer1,
              answer2: values.answer2,
              answer3: values.answer3,
              answer4: values.answer4,
              answer5: values.answer5,
              answer6: values.answer6,
              observations: values.observations,
              signer: values.signer,
              signatureUtFileKey: values.signatureUtFileKey,
              imagesUtFileKeys: values.imagesUtFileKeys,
              from: `${user.firstName} ${user.lastName}`,
            });
          }}
        />
      ),
    });
  };

  return (
    <Stack>
      {serviceOrderRequests.length > 0 && (
        <Stack>
          {serviceOrderRequests.map(request => (
            <Group key={request.id} align="center" justify="space-between">
              <Stack gap={0} style={{ flex: 1 }}>
                <Text size="sm" fw={500}>
                  Fiche du {formatDate(request.date || request.createdAt)}{' '}
                  {request.time ? `à ${request.time}` : ''}
                </Text>
                <Text c="dimmed" size="xs">
                  {request.question1 || 'Aucune information'}
                </Text>
              </Stack>
              <Button size="xs" onClick={() => openFillOutModal(request)}>
                Remplir
              </Button>
            </Group>
          ))}
        </Stack>
      )}

      {serviceOrderDocuments.length > 0 && (
        <>
          {serviceOrderRequests.length > 0 && <Divider />}
          <Stack gap="md">
            <Text size="sm" fw={700}>
              <b>Fiches d'intervention remplies</b>
            </Text>
            {reverse([...serviceOrderDocuments]).map((document, index) => (
              <Group key={document.id} align="center" justify="space-between">
                <Stack gap={0}>
                  <Text size="sm">Fiche n°{index + 1}</Text>
                  <Text c="dimmed" size="xs" truncate style={{ flex: 1 }}>
                    le {formatDate(document.createdAt)}
                  </Text>
                </Stack>
                <Group gap="xs">
                  <Button
                    leftSection={<IconEye size={16} />}
                    variant="outline"
                    size="xs"
                    onClick={() => {
                      window.open(
                        getUTDocumentUrl(document.utFileKey),
                        '_blank',
                      );
                    }}
                  >
                    Voir
                  </Button>
                  <Button
                    variant="subtle"
                    color="red"
                    size="xs"
                    onClick={async () => {
                      if (
                        confirm(
                          "Voulez-vous vraiment supprimer cette fiche d'intervention ?\n\nCette action est irréversible.",
                        )
                      ) {
                        await deleteServiceOrder.mutateAsync({
                          documentId: document.id,
                        });
                      }
                    }}
                  >
                    <IconTrash size={14} />
                  </Button>
                </Group>
              </Group>
            ))}
          </Stack>
        </>
      )}
    </Stack>
  );
}
